<template>
  <b-sidebar
    id="add-new-user-sidebar"
    :visible="isAddNewUserSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @hidden="resetForm"
    @change="showAddUserSidebar($event)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          Formulário Usuário
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />

      </div>

      <!-- BODY -->
      <validation-observer
        #default="{ handleSubmit }"
        ref="refFormObserver"
      >
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >

          <!-- Tipo de Pessoa -->
          <validation-provider
            #default="validationContext"
            name="Tipo de Pessoa"
            rules="required"
          >
            <b-form-group
              label="Tipo de Pessoa"
              label-for="tipo-pessoa"
            >
              <b-form-radio-group
                id="tipo-pessoa"
                v-model="userData.tipo_pessoa"
                :options="tiposPessoa"
                :state="getValidationState(validationContext)"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Dados Pessoais - Pessoa Física -->
          <div v-if="userData.tipo_pessoa === 'F'">
            <h6 class="mt-3 mb-2">Dados Pessoais</h6>
            
            <!-- Nome -->
            <validation-provider
              #default="validationContext"
              name="Nome"
              rules="required"
            >
              <b-form-group
                label="Nome"
                label-for="nome-responsavel"
              >
                <b-form-input
                  id="nome-responsavel"
                  v-model="userData.nome_responsavel"
                  :state="getValidationState(validationContext)"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Sobrenome -->
            <validation-provider
              #default="validationContext"
              name="Sobrenome"
              rules="required"
            >
              <b-form-group
                label="Sobrenome"
                label-for="sobrenome"
              >
                <b-form-input
                  id="sobrenome"
                  v-model="userData.sobrenome"
                  :state="getValidationState(validationContext)"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- CPF -->
            <validation-provider
              #default="validationContext"
              name="CPF"
              rules="required"
            >
              <b-form-group
                label="CPF"
                label-for="cpf"
              >
                <b-form-input
                  id="cpf"
                  v-model="userData.cpf"
                  :state="getValidationState(validationContext)"
                  v-mask="'###.###.###-##'"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- RG -->
            <b-form-group
              label="RG"
              label-for="rg"
            >
              <b-form-input
                id="rg"
                v-model="userData.rg"
              />
            </b-form-group>

            <!-- Órgão Expedidor -->
            <b-form-group
              label="Órgão Expedidor"
              label-for="orgao-expedidor"
            >
              <b-form-input
                id="orgao-expedidor"
                v-model="userData.orgao_expedidor"
              />
            </b-form-group>            <!-- Data de Nascimento -->
            <b-form-group
              label="Data de Nascimento"
              label-for="data-nascimento"
            >
              <b-form-input
                id="data-nascimento"
                v-model="dataNascimentoFormatted"
                type="date"
              />
            </b-form-group>

            <!-- Sexo -->
            <b-form-group
              label="Sexo"
              label-for="sexo"
            >
              <b-form-radio-group
                id="sexo"
                v-model="userData.sexo"
                :options="sexoOptions"
              />
            </b-form-group>
          </div>

          <!-- Dados Pessoais - Pessoa Jurídica -->
          <div v-else-if="userData.tipo_pessoa === 'J'">
            <h6 class="mt-3 mb-2">Dados da Empresa</h6>

            <!-- CNPJ -->
            <validation-provider
              #default="validationContext"
              name="CNPJ"
              rules="required"
            >
              <b-form-group
                label="CNPJ"
                label-for="cnpj"
              >
                <b-form-input
                  id="cnpj"
                  v-model="userData.cnpj"
                  :state="getValidationState(validationContext)"
                  v-mask="'##.###.###/####-##'"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Razão Social -->
            <validation-provider
              #default="validationContext"
              name="Razão Social"
              rules="required"
            >
              <b-form-group
                label="Razão Social"
                label-for="razao-social"
              >
                <b-form-input
                  id="razao-social"
                  v-model="userData.razao_social"
                  :state="getValidationState(validationContext)"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Nome Fantasia -->
            <b-form-group
              label="Nome Fantasia"
              label-for="nome-fantasia"
            >
              <b-form-input
                id="nome-fantasia"
                v-model="userData.nome_fantasia"
              />
            </b-form-group>

            <!-- Inscrição Estadual -->
            <b-form-group
              label="Inscrição Estadual"
              label-for="inscricao-estadual"
            >
              <b-form-input
                id="inscricao-estadual"
                v-model="userData.inscricao_estadual"
              />
            </b-form-group>

            <!-- Data de Fundação -->
            <b-form-group
              label="Data de Fundação"
              label-for="data-fundacao"
            >
              <b-form-input
                id="data-fundacao"
                v-model="userData.data_fundacao"
                type="date"
              />
            </b-form-group>

            <!-- Nome do Responsável -->
            <validation-provider
              #default="validationContext"
              name="Nome do Responsável"
              rules="required"
            >
              <b-form-group
                label="Nome do Responsável"
                label-for="nome-responsavel-pj"
              >
                <b-form-input
                  id="nome-responsavel-pj"
                  v-model="userData.nome_responsavel"
                  :state="getValidationState(validationContext)"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </div>

          <!-- Contato -->
          <h6 class="mt-3 mb-2">Contato</h6>

          <!-- Email/Login -->
          <validation-provider
            #default="validationContext"
            name="Email"
            rules="required|email"
          >
            <b-form-group
              label="Login/Email"
              label-for="login"
            >
              <b-form-input
                id="login"
                v-model="userData.login"
                :state="getValidationState(validationContext)"
                trim
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Celular -->
          <validation-provider
            #default="validationContext"
            name="Celular"
            rules="required"
          >
            <b-form-group
              label="Celular"
              label-for="celular"
            >
              <b-form-input
                id="celular"
                v-model="userData.celular"
                :state="getValidationState(validationContext)"
                v-mask="'(##) #####-####'"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Senha -->
          <validation-provider
            v-if="blankUserData.id === null"
            #default="validationContext"
            name="Senha"
            rules="required|min:6"
          >
            <b-form-group
              label="Senha"
              label-for="senha-usuario"
            >
              <b-form-input
                id="senha-usuario"
                v-model="userData.senha"
                type="password"
                :state="getValidationState(validationContext)"
                trim
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Endereço -->
          <h6 class="mt-3 mb-2">Endereço</h6>

          <!-- CEP -->
          <b-form-group
            label="CEP"
            label-for="cep"
          >
            <b-form-input
              id="cep"
              v-model="userData.cep"
              v-mask="'#####-###'"
              @blur="buscarCep"
            />
          </b-form-group>

          <!-- Rua -->
          <b-form-group
            label="Rua"
            label-for="rua"
          >
            <b-form-input
              id="rua"
              v-model="userData.rua"
            />
          </b-form-group>

          <!-- Número e Complemento -->
          <b-row>
            <b-col md="6">
              <b-form-group
                label="Número"
                label-for="numero"
              >
                <b-form-input
                  id="numero"
                  v-model="userData.numero"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                label="Complemento"
                label-for="complemento"
              >
                <b-form-input
                  id="complemento"
                  v-model="userData.complemento"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Bairro -->
          <b-form-group
            label="Bairro"
            label-for="bairro"
          >
            <b-form-input
              id="bairro"
              v-model="userData.bairro"
            />
          </b-form-group>

          <!-- Cidade e Estado -->
          <b-row>
            <b-col md="8">
              <b-form-group
                label="Cidade"
                label-for="cidade"
              >
                <b-form-input
                  id="cidade"
                  v-model="userData.cidade"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                label="Estado"
                label-for="estado"
              >
                <b-form-select
                  id="estado"
                  v-model="userData.estado"
                  :options="estadosOptions"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Dados Bancários -->
          <h6 class="mt-3 mb-2">Dados Bancários</h6>

          <!-- Comissão Padrinho -->
          <validation-provider
            #default="validationContext"
            name="Comissão Padrinho"
            rules="required"
          >
            <b-form-group
              label="Comissão Padrinho (%)"
              label-for="comissao-padrinho"
            >
              <b-form-input
                id="comissao-padrinho"
                v-model="userData.comissao_padrinho"
                type="number"
                step="0.01"
                min="0"
                max="100"
                :state="getValidationState(validationContext)"
                placeholder="0.00"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Validade da Comissão -->
          <b-form-group
            label="Validade da Comissão"
            label-for="comissao-validade"
          >
            <b-form-input
              id="comissao-validade"
              v-model="userData.comissao_validade"
              type="date"
              placeholder="Selecione a data de validade"
            />
            <small class="text-muted">
              Data até quando a comissão configurada será válida
            </small>
          </b-form-group>

          <!-- Banco -->
          <b-form-group
            label="Banco"
            label-for="banco"
          >
            <b-form-input
              id="banco"
              v-model="userData.banco"
            />
          </b-form-group>

          <!-- Agência -->
          <b-row>
            <b-col md="8">
              <b-form-group
                label="Agência"
                label-for="agencia"
              >
                <b-form-input
                  id="agencia"
                  v-model="userData.agencia"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                label="Dígito"
                label-for="digito-agencia"
              >
                <b-form-input
                  id="digito-agencia"
                  v-model="userData.digito_agencia"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Conta -->
          <b-row>
            <b-col md="8">
              <b-form-group
                label="Conta"
                label-for="conta"
              >
                <b-form-input
                  id="conta"
                  v-model="userData.conta"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                label="Dígito"
                label-for="digito-conta"
              >
                <b-form-input
                  id="digito-conta"
                  v-model="userData.digito_conta"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- User Grupo Usuario -->
          <validation-provider
            #default="validationContext"
            name="Grupo Usuario"
            rules="required"
          >
            <b-form-group
              label="Grupo Usuario"
              label-for="id-grupo-usuario"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="userData.id_grupousuario"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="gruposOptions"
                label="grupousuario"
                :reduce="val => val.id"
                :clearable="false"
                input-id="id-grupo-usuario"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Status Ativo -->
          <b-form-group>
            <b-form-checkbox
              id="ativo"
              v-model="userData.ativo"
              :value="1"
              :unchecked-value="0"
            >
              Usuário Ativo
            </b-form-checkbox>
          </b-form-group>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
            >
              {{ userData.id === null ? 'Adicionar' : 'Atualizar' }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
            >
              Cancelar
            </b-button>
          </div>

        </b-form>
      </validation-observer>
    </template>

  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormInput, BFormInvalidFeedback, BButton,
  BFormRadioGroup, BFormSelect, BFormCheckbox, BRow, BCol,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { computed } from '@vue/composition-api'
import { required, alphaNum, email } from '@validations'
import formValidation from '@core/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import store from '@/store'
// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BFormRadioGroup,
    BFormSelect,
    BFormCheckbox,
    BRow,
    BCol,
    vSelect,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddNewUserSidebarActive',
    event: 'update:is-add-new-usuarios-sidebar-active',
  },
  props: {
    blankUserData: {
      type: Object,
      required: true,
    },
    isAddNewUserSidebarActive: {
      type: Boolean,
      required: true,
    },
    gruposOptions: {
      type: Array,
      required: true,
    },
    planOptions: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      tiposPessoa: [
        { text: 'Pessoa Física', value: 'F' },
        { text: 'Pessoa Jurídica', value: 'J' },
      ],
      sexoOptions: [
        { text: 'Masculino', value: 'M' },
        { text: 'Feminino', value: 'F' },
      ],
      estadosOptions: [
        { text: 'Selecione...', value: null },
        { text: 'Acre', value: 'AC' },
        { text: 'Alagoas', value: 'AL' },
        { text: 'Amapá', value: 'AP' },
        { text: 'Amazonas', value: 'AM' },
        { text: 'Bahia', value: 'BA' },
        { text: 'Ceará', value: 'CE' },
        { text: 'Distrito Federal', value: 'DF' },
        { text: 'Espírito Santo', value: 'ES' },
        { text: 'Goiás', value: 'GO' },
        { text: 'Maranhão', value: 'MA' },
        { text: 'Mato Grosso', value: 'MT' },
        { text: 'Mato Grosso do Sul', value: 'MS' },
        { text: 'Minas Gerais', value: 'MG' },
        { text: 'Pará', value: 'PA' },
        { text: 'Paraíba', value: 'PB' },
        { text: 'Paraná', value: 'PR' },
        { text: 'Pernambuco', value: 'PE' },
        { text: 'Piauí', value: 'PI' },
        { text: 'Rio de Janeiro', value: 'RJ' },
        { text: 'Rio Grande do Norte', value: 'RN' },
        { text: 'Rio Grande do Sul', value: 'RS' },
        { text: 'Rondônia', value: 'RO' },
        { text: 'Roraima', value: 'RR' },
        { text: 'Santa Catarina', value: 'SC' },
        { text: 'São Paulo', value: 'SP' },
        { text: 'Sergipe', value: 'SE' },
        { text: 'Tocantins', value: 'TO' },
      ],
    }
  },
  methods: {
    showAddUserSidebar(e) {
      this.$emit('input', e)
    },
    async buscarCep() {
      if (this.userData.cep && this.userData.cep.length === 9) {
        try {
          const cep = this.userData.cep.replace('-', '')
          const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
          const data = await response.json()
          
          if (!data.erro) {
            this.userData.rua = data.logradouro
            this.userData.bairro = data.bairro
            this.userData.cidade = data.localidade
            this.userData.estado = data.uf
          }
        } catch (error) {
          console.error('Erro ao buscar CEP:', error)
        }
      }
    },
  },
  setup(props, { emit }) {
    const toast = useToast()
    const userData = computed({
      get: () => {
        if (props.blankUserData) {
          return props.blankUserData
        }
        return JSON.parse(JSON.stringify(props.blankUserData))
      },
      set: val => {
        emit('update:blankUserData', val)
      },
    })
    
    const resetuserData = () => {
      userData.value = JSON.parse(JSON.stringify(userData.value))
    }

    // Computed property para formatar a data de nascimento
    const dataNascimentoFormatted = computed({
      get() {
        if (userData.value.data_nascimento) {
          // Se a data está no formato ISO, extrair apenas a parte da data (YYYY-MM-DD)
          const dateStr = userData.value.data_nascimento.toString()
          if (dateStr.includes('T')) {
            return dateStr.split('T')[0]
          }
          return dateStr
        }
        return ''
      },
      set(value) {
        userData.value.data_nascimento = value
      },
    })

    const onSubmit = () => {
      if (userData.value.id === null) {
        store.dispatch('app-adm-usuario/addUser', {
          ...userData.value,
        })
          .then(() => {
            emit('refetch-data')
            emit('input', false)
          })
          .catch(erro => {
            toast({
              component: ToastificationContent,
              props: {
                title: erro.response.data.message,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          })
      } else {
        store.dispatch('app-adm-usuario/updatedUser', {
          ...userData.value,
        })
          .then(() => {
            emit('refetch-data')
            emit('input', false)
          })
          .catch(erro => {
            toast({
              component: ToastificationContent,
              props: {
                title: erro.response.data.message,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          })
      }
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetuserData)

    return {
      userData,
      dataNascimentoFormatted,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetForm,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

#add-new-user-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
